import React, { useEffect, useRef, useState } from "react";
import AvatarEditor from "react-avatar-edit";
import loadImage from "blueimp-load-image";
import { useProfile } from "../../context/ProfileContext";
import api from "../../utils/api";
import { apiUrls } from "../../utils/urls";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import Upload from "@mui/icons-material/Upload";
import Typography from "@mui/material/Typography";

const ProfilePictureUpload = ({
  isOpen,
  onClose,
  initialImage = null,
  title = "Edit Profile Picture",
  onSubmit = null,
  fieldName = "profile_pic",
}) => {
  const { refetchUser } = useProfile();
  const [preview, setPreview] = useState(null);
  const [editorSrc, setEditorSrc] = useState(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const objectUrlRef = useRef(null);

  const loadImageWithOrientation = (source) =>
    new Promise((resolve, reject) => {
      loadImage(
        source,
        (img) => {
          if (!img || img.type === "error") {
            reject(new Error("Failed to load image"));
            return;
          }
          resolve(img);
        },
        { orientation: true, canvas: true, meta: true }
      );
    });

  useEffect(() => {
    if (!isOpen) {
      setEditorSrc(null);
      setIsLoadingTemplate(false);
      return;
    }

    if (!initialImage) {
      setEditorSrc(null);
      setIsLoadingTemplate(false);
      return;
    }

    if (initialImage.startsWith("data:") || initialImage.startsWith("blob:")) {
      setEditorSrc(initialImage);
      setIsLoadingTemplate(false);
      return;
    }

    let isCancelled = false;
    setIsLoadingTemplate(true);

    fetch(initialImage, { mode: "cors" })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load template image: ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        if (isCancelled) return;
        return loadImageWithOrientation(blob)
          .then((image) => {
            if (isCancelled) return;
            const normalizedSrc =
              image?.toDataURL?.("image/jpeg", 0.95) || null;
            if (!normalizedSrc) {
              throw new Error("Failed to normalize image data");
            }
            setEditorSrc(normalizedSrc);
            setIsLoadingTemplate(false);
          })
          .catch((error) => {
            console.error("Error normalizing template image:", error);
            if (objectUrlRef.current) {
              URL.revokeObjectURL(objectUrlRef.current);
            }
            const objectUrl = URL.createObjectURL(blob);
            objectUrlRef.current = objectUrl;
            setEditorSrc(objectUrl);
            setIsLoadingTemplate(false);
          });
      })
      .catch((error) => {
        console.error("Error loading template image:", error);
        setEditorSrc(null);
        setIsLoadingTemplate(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [initialImage, isOpen]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  const handleEditorClose = () => {
    setPreview(null);
  };

  const handleCrop = (view) => {
    setPreview(view);
  };

  const handleUpload = async () => {
    if (!preview) return;

    const response = await fetch(preview);
    const blob = await response.blob();

    // If custom onSubmit is provided, use it
    if (onSubmit) {
      await onSubmit(blob);
      onClose();
      return;
    }

    // Default behavior for user profile pictures
    const formData = new FormData();
    formData.append(fieldName, blob, `${fieldName}.png`);

    try {
      const apiResponse = await api.post(apiUrls.users.profile(), formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (apiResponse.data.success) {
        refetchUser();
      }
      onClose();
    } catch (error) {
      console.error("Error uploading profile picture:", error);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "center",
            minHeight: 295,
            alignItems: "center",
          }}
        >
          {isLoadingTemplate ? (
            <Typography variant="body2" color="text.secondary">
              Loading template image…
            </Typography>
          ) : (
            <AvatarEditor
              key={editorSrc || "empty"}
              width={390}
              height={295}
              onCrop={handleCrop}
              onClose={handleEditorClose}
              {...(editorSrc ? { src: editorSrc } : {})}
              crossOrigin="anonymous"
              label={
                <Button
                  startIcon={<Upload />}
                  onClick={(event) => event.target.parentElement.click()}
                >
                  Upload Image
                </Button>
              }
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="standard">
          Cancel
        </Button>
        {preview && (
          <Button onClick={handleUpload} variant="standard">
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProfilePictureUpload;
